import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Logo from '../assets/favicon.jpeg';
import './LegalTheme.css';

const css = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --orange: #F1561D;
    --green: #0cb44aff;
    --orange-dark: #C94414;
    --orange-light: #FF7A45;
    --orange-pale: #FFF0EB;
    --black: #0D0D0D;
    --dark: #1A1A1A;
    --grey: #6B6B6B;
    --light-grey: #F5F4F2;
    --white: #FFFFFF;
    --font-display: 'Syne', sans-serif;
    --font-body: 'DM Sans', sans-serif;
  }

  html { scroll-behavior: smooth; }
  body { font-family: var(--font-body); background: var(--white); color: var(--black); overflow-x: hidden; }

  nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 100;
    display: flex; align-items: center; justify-content: space-between;
    padding: 16px 48px;
    background: rgba(255,255,255,0.92);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid rgba(0,0,0,0.06);
    gap: 12px;
  }

  .nav-logo {
    display: flex; align-items: center; gap: 10px;
    font-family: var(--font-display); font-size: 18px; font-weight: 800;
    color: var(--black); text-decoration: none;
    white-space: nowrap; flex-shrink: 0;
  }

  .nav-back {
    background: transparent; color: var(--black);
    border: 1.5px solid rgba(0,0,0,.15); border-radius: 100px;
    padding: 10px 20px; font-size: 13px; font-weight: 600; cursor: pointer;
    font-family: var(--font-body); transition: all .2s;
    display: inline-flex; align-items: center; gap: 6px;
    text-decoration: none;
  }
  .nav-back:hover { border-color: var(--orange); color: var(--orange); }

  .page-wrap {
    max-width: 760px;
    margin: 0 auto;
    padding: 136px 24px 100px;
  }

  .page-icon {
    display: flex;
    justify-content: center;
    margin-bottom: 28px;
  }

  .page-title {
    font-family: var(--font-display);
    font-size: clamp(32px, 5vw, 48px);
    font-weight: 800;
    color: var(--black);
    text-align: center;
    line-height: 1.1;
    letter-spacing: -.02em;
    margin-bottom: 8px;
  }

  .page-date {
    font-size: 13px;
    color: var(--grey);
    text-align: center;
    margin-bottom: 48px;
  }

  .page-divider {
    height: 1px;
    background: rgba(0,0,0,.08);
    margin-bottom: 48px;
  }

  .policy-section {
    margin-bottom: 36px;
  }

  .policy-section-title {
    font-family: var(--font-display);
    font-size: 18px;
    font-weight: 700;
    color: var(--orange);
    margin-bottom: 12px;
  }

  .policy-section-body {
    font-size: 15px;
    font-weight: 300;
    color: #444;
    line-height: 1.8;
  }

  .policy-bullets {
    background: var(--light-grey);
    border-radius: 16px;
    padding: 20px 24px;
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .policy-bullet {
    display: flex;
    gap: 12px;
    align-items: flex-start;
  }

  .policy-bullet-dot {
    color: var(--orange);
    font-weight: 700;
    font-size: 16px;
    flex-shrink: 0;
    line-height: 1.6;
  }

  .policy-bullet-text {
    font-size: 14px;
    color: #333;
    line-height: 1.7;
  }

  .policy-sub-label {
    font-weight: 600;
    font-size: 14px;
    color: var(--black);
    margin: 18px 0 10px;
  }

  .contact-link {
    color: var(--orange);
    text-decoration: none;
    font-weight: 500;
  }
  .contact-link:hover { text-decoration: underline; }

  footer {
    background: var(--light-grey);
    padding: 40px 48px;
    border-top: 1px solid rgba(0,0,0,.06);
    text-align: center;
  }
  .footer-copy { font-size: 13px; color: var(--grey); }
  .footer-links-row {
    display: flex; gap: 24px; justify-content: center; margin-bottom: 16px; flex-wrap: wrap;
  }
  .footer-links-row a {
    font-size: 13px; color: var(--grey); text-decoration: none; transition: color .2s;
  }
  .footer-links-row a:hover { color: var(--orange); }

  @keyframes fade-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
  .animate-in { animation: fade-up .5s ease both; }
  .animate-in-delay { animation: fade-up .5s .1s ease both; }

  @media (max-width: 600px) {
    nav { padding: 12px 16px; }
    .nav-logo { font-size: 15px; }
    .nav-logo img { width: 30px !important; height: 30px !important; }
    .page-wrap { padding: 110px 16px 80px; }
    footer { padding: 32px 16px; }
    .policy-bullets { padding: 16px; }
  }
`;

const TermsPage: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);

    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Manrope:wght@600;700;800&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  return (
    <div className="legal-page">
      <Helmet>
        <title>Terms of Service — JAC ScanPayGo</title>
        <meta name="description" content="The terms and conditions governing your use of JAC ScanPayGo." />
        <link rel="canonical" href="https://www.jacscanpaygo.co.za/terms" />
      </Helmet>

      <style>{css}</style>

      {/* ── NAV ── */}
      <nav>
        <a href="/" className="nav-logo">
          <img
            src={Logo}
            alt="Logo"
            style={{ width: 36, height: 36, borderRadius: 10, objectFit: 'cover' }}
          />
          <span>JAC <span className="brand-accent">ScanPayGo</span></span>
        </a>
        <button className="nav-back" onClick={() => navigate(-1)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M19 12H5M12 5l-7 7 7 7" />
          </svg>
          Back
        </button>
      </nav>

      {/* ── CONTENT ── */}
      <div className="page-wrap">

        <div className="page-icon animate-in">
          <svg width="72" height="72" viewBox="0 0 72 72" fill="none">
            <circle cx="36" cy="36" r="36" fill="#FFF0EB" />
            <rect x="20" y="16" width="32" height="40" rx="5" fill="#F1561D" opacity=".2" />
            <rect x="20" y="16" width="32" height="40" rx="5" stroke="#F1561D" strokeWidth="2" />
            <line x1="27" y1="27" x2="45" y2="27" stroke="#F1561D" strokeWidth="2" strokeLinecap="round" />
            <line x1="27" y1="34" x2="45" y2="34" stroke="#F1561D" strokeWidth="2" strokeLinecap="round" />
            <line x1="27" y1="41" x2="37" y2="41" stroke="#F1561D" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>

        <h1 className="page-title animate-in">Terms of Service</h1>
        <div className="page-date animate-in-delay">Last Updated: December 2025</div>
        <div className="page-divider" />

        <div className="policy-section">
          <div className="policy-section-title">1. Acceptance of Terms</div>
          <div className="policy-section-body">
            By downloading, installing, or using the JAC ScanPayGo application or any related services, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any part of these terms, you may not use our service. These terms apply to all users, including shoppers and store operators.
          </div>
        </div>

        <div className="policy-section">
          <div className="policy-section-title">2. Description of Service</div>
          <div className="policy-section-body">
            JAC ScanPayGo is a mobile self-checkout platform that allows shoppers to scan retail products using their smartphone camera, build a cart, and pay via Paystack — without using a traditional cashier lane. The platform also provides store owners and managers with an admin dashboard for product management, transaction monitoring, and sales analytics.
          </div>
        </div>

        <div className="policy-section">
          <div className="policy-section-title">3. User Accounts</div>
          <div className="policy-section-body">
            To use JAC ScanPayGo, you must create an account. You are responsible for maintaining the confidentiality of your credentials and for all activity that occurs under your account.
          </div>
          <div className="policy-bullets">
            {[
              'You must provide accurate, current, and complete information during registration.',
              'You must be at least 18 years old, or have parental/guardian consent, to create an account.',
              'You must not share your account with any other person.',
              'You must notify us immediately of any unauthorized access to your account.',
            ].map((text, i) => (
              <div className="policy-bullet" key={i}>
                <span className="policy-bullet-dot">•</span>
                <span className="policy-bullet-text">{text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="policy-section">
          <div className="policy-section-title">4. Acceptable Use</div>
          <div className="policy-section-body">
            You agree to use JAC ScanPayGo only for its intended purpose — legitimate retail self-checkout. The following actions are strictly prohibited:
          </div>
          <div className="policy-bullets">
            {[
              'Scanning items you do not intend to purchase or removing items from your cart after physically keeping them.',
              'Attempting to spoof your GPS location to check into a store you are not physically present at.',
              'Exploiting bugs, vulnerabilities, or unintended behaviours in the app or backend systems.',
              'Reverse engineering, decompiling, or attempting to extract source code from the application.',
              'Using the platform for any fraudulent, unlawful, or deceptive purpose.',
            ].map((text, i) => (
              <div className="policy-bullet" key={i}>
                <span className="policy-bullet-dot">•</span>
                <span className="policy-bullet-text">{text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="policy-section">
          <div className="policy-section-title">5. Payments &amp; Transactions</div>
          <div className="policy-section-body">
            All payments through JAC ScanPayGo are processed securely by Paystack, our third-party payment provider. By initiating a payment, you authorise the transaction amount for the items in your cart.
          </div>
          <div className="policy-sub-label">Important payment terms:</div>
          <div className="policy-bullets">
            {[
              'Once a payment is confirmed, the transaction is considered complete and the items in your cart are purchased.',
              'Refunds or disputes must be raised with the store directly, or via our support team at support@jacscanpaygo.co.za.',
              'JAC ScanPayGo does not store your card details. All card data is handled by Paystack under their own security standards.',
              'You are responsible for ensuring sufficient funds are available before initiating checkout.',
            ].map((text, i) => (
              <div className="policy-bullet" key={i}>
                <span className="policy-bullet-dot">•</span>
                <span className="policy-bullet-text">{text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="policy-section">
          <div className="policy-section-title">6. Store Owner Responsibilities</div>
          <div className="policy-section-body">
            If you are a store owner or manager using the JAC ScanPayGo admin platform, you agree to the following additional obligations:
          </div>
          <div className="policy-bullets">
            {[
              'Ensure that all product barcodes and prices in the system are accurate and up to date.',
              'Maintain appropriate oversight of self-checkout sessions occurring in your store.',
              'Not use the admin system to access data beyond what is necessary for your store operations.',
              'Keep your admin credentials confidential and not share access with unauthorised personnel.',
              'Comply with all applicable consumer protection and retail regulations in South Africa.',
            ].map((text, i) => (
              <div className="policy-bullet" key={i}>
                <span className="policy-bullet-dot">•</span>
                <span className="policy-bullet-text">{text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="policy-section">
          <div className="policy-section-title">7. Intellectual Property</div>
          <div className="policy-section-body">
            All content, branding, source code, design, and functionality of JAC ScanPayGo — including but not limited to the app interface, logo, admin dashboard, and API — are the exclusive intellectual property of JAC ScanPayGo and its founder. You may not reproduce, distribute, or create derivative works from any part of the platform without prior written permission.
          </div>
        </div>

        <div className="policy-section">
          <div className="policy-section-title">8. Service Availability</div>
          <div className="policy-section-body">
            JAC ScanPayGo is provided on an "as is" and "as available" basis. While we strive for high uptime and reliability, we do not guarantee uninterrupted, error-free, or continuous operation. Scheduled maintenance, third-party outages (including Paystack), or unforeseen technical issues may temporarily affect service availability. We are not liable for losses incurred during service interruptions.
          </div>
        </div>

        <div className="policy-section">
          <div className="policy-section-title">9. Limitation of Liability</div>
          <div className="policy-section-body">
            To the maximum extent permitted by applicable South African law, JAC ScanPayGo and its founder shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of — or inability to use — the platform. This includes, but is not limited to, loss of revenue, loss of data, or losses resulting from unauthorized account access.
          </div>
        </div>

        <div className="policy-section">
          <div className="policy-section-title">10. Account Suspension &amp; Termination</div>
          <div className="policy-section-body">
            We reserve the right to suspend or permanently terminate any account that violates these Terms of Service, engages in fraudulent activity, or misuses the platform in any way. In serious cases — such as suspected shoplifting or payment fraud — we may share relevant session data with store owners or law enforcement.
          </div>
        </div>

        <div className="policy-section">
          <div className="policy-section-title">11. Changes to These Terms</div>
          <div className="policy-section-body">
            JAC ScanPayGo may update these Terms of Service from time to time. When changes are made, the "Last Updated" date at the top of this page will be revised. Continued use of the application after changes are published constitutes your acceptance of the updated terms. We encourage you to review this page periodically.
          </div>
        </div>

        <div className="policy-section">
          <div className="policy-section-title">12. Governing Law</div>
          <div className="policy-section-body">
            These Terms of Service are governed by and construed in accordance with the laws of the Republic of South Africa. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the South African courts.
          </div>
        </div>

        <div className="policy-section">
          <div className="policy-section-title">13. Contact Information</div>
          <div className="policy-section-body">
            If you have any questions about these Terms of Service, please contact us at{' '}
            <a href="mailto:support@jacscanpaygo.co.za" className="contact-link">support@jacscanpaygo.co.za</a>. We aim to respond to all enquiries within 5 business days.
          </div>
        </div>

      </div>

      {/* ── FOOTER ── */}
      <footer>
        <div className="footer-links-row">
          <a href="/privacy">Privacy Policy</a>
          <a href="/terms">Terms of Service</a>
          <a href="mailto:support@jacscanpaygo.co.za">support@jacscanpaygo.co.za</a>
          <a href="/">Home</a>
        </div>
        <div className="footer-copy">© {new Date().getFullYear()} JAC ScanPayGo. All rights reserved.</div>
      </footer>
    </div>
  );
};

export default TermsPage;
